import { getFormattedAmount } from '@zonofi/common';
import { LoadingSpinner, SVGS } from '@zonofi/zono-money-design-system';
import {
  OrderData,
  OrderDataLine,
  OrderStatusEnum,
  useAppSelector,
} from '@zonofi/zono-money-helpers';
import React, { useMemo } from 'react';
import { useOrderDetailsList } from './useOrderDetailsList';
import { getCustomerInvite, PricingStrategy } from '@zonofi/zono-money-store';
import { isNumber } from 'lodash';
import { TailSpin } from 'react-loader-spinner';
import { WeightedPriceInfo } from '../weighted-price-info/weighted-price-info';

interface OrderDetailsListProps {
  orderDetails: OrderData;
  onIncrement: (
    val: OrderDataLine,
    sellerWorkspaceId: string,
    customerId: string
  ) => any;
  onDecrement: (val: any, sellerWorkspaceId: string, customerId: string) => any;
  onDelete: (val: any, sellerWorkspaceId: string, customerId: string) => any;
  onChangeQuantity: (
    productVariantId: any,
    qty: number,
    sellerWorkspaceId: string,
    customerId: string
  ) => any;
  showImage: boolean;
  showPrice: boolean;
  showOrderEdit: boolean;
  showOutOfStock?: boolean;
  isCart?: boolean;
}

export const OrderDetailsList: React.FC<OrderDetailsListProps> = ({
  orderDetails,
  onIncrement,
  onDecrement,
  onDelete,
  onChangeQuantity,
  showImage,
  showPrice,
  showOrderEdit,
  showOutOfStock = false,
  isCart,
}) => {
  const inviteDetails = useAppSelector(getCustomerInvite);
  const { seller, inviteId } = inviteDetails;

  const {
    getModifiedImageUrl,
    quantityMap,
    setQuantityMap,
    openProductDetails,
    hidePrice,
  } = useOrderDetailsList(orderDetails, isCart, showImage, showPrice);

  const showPrices = useMemo(
    () =>
      [
        OrderStatusEnum.DRAFT,
        OrderStatusEnum.ADDING_ITEMS,
        OrderStatusEnum.ORDER_CREATED,
        OrderStatusEnum.SUBMITTED_BY_SALES,
        OrderStatusEnum.SUBMITTED_BY_CFA,
        OrderStatusEnum.SUBMITTED_BY_CUSTOMER,
        OrderStatusEnum.SUBMITTED,
        OrderStatusEnum.ORDER_SUBMITTED,
        OrderStatusEnum.SUBMITTEDBYCUSTOMER,
        OrderStatusEnum.SUBMITTED_2,
      ].includes(orderDetails?.status as any)
        ? showPrice
        : true,
    [orderDetails, showPrice]
  );

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    item: OrderDataLine
  ) => {
    // Prevent image/button default interaction quirks in some mobile webviews.
    e.preventDefault();
    e.stopPropagation();
    onDelete(item, seller?.workspaceId, inviteId);
  };

  return orderDetails?.lines?.map((item, index) => {
    const isOutOfStock = Number(item?.stock ?? 1) < 1;
    const shouldDisableQtyControl = showOutOfStock && isOutOfStock;
    const quantityKey = item?.poFileLineId ?? item?.orderLineId ?? '';

    return (
      <div className="order-details-line-item-root" key={index}>
        <div className="order-details-line-item">
          <div className="order-details-line-item-container">
            {showImage ? (
              <img
                src={
                  item?.productImage
                    ? getModifiedImageUrl(item?.productImage)
                    : SVGS.DefaultProduct
                }
                className="order-details-line-item-default-img"
                alt=""
                onClick={() => openProductDetails(item?.productVariantId)}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = SVGS.DefaultProduct;
                }}
              />
            ) : (
              <></>
            )}
            <div className="order-details-line-item-container-info">
              <span className="font-primary">{item?.title}</span>
              <div className="order-details-line-item-container-info-facets">
                {item?.facets?.map((f, i) => {
                  return (
                    <span key={i}>
                      {f?.facetName}: {f?.facetValue}
                    </span>
                  );
                })}
              </div>
              {!isNumber(item.productVariantId) && (
                <span className="font-primary order-details-line-item-container-info-unmapped-product">
                  Unmapped
                </span>
              )}
              {item?.promotionNames?.length > 0 && (
                <div className="order-details-line-item-container-info-promotion">
                  <img
                    src={SVGS.DiscountIcon}
                    alt=""
                    className="order-details-line-item-container-info-promotion-icon"
                  />
                  <span className="order-details-line-item-container-info-promotion-text font-primary-semibold">
                    {item.promotionNames.join(', ')} applied!
                  </span>
                </div>
              )}
              {showPrices && (
                <span className="order-details-list-item-info-price-container font-primary-semibold">
                  {getFormattedAmount(item?.price ?? 0) ?? 0}
                  {item?.pricingStrategy === PricingStrategy.WEIGHT_BASED && (
                    <WeightedPriceInfo weightedPrice={item?.weightedPrice} />
                  )}
                </span>
              )}
              {showOutOfStock && isOutOfStock && (
                <div className="order-details-list-item-out-of-stock-tag">
                  <span className="font-primary">OUT OF STOCK</span>
                </div>
              )}
              {!showOrderEdit ? (
                <div className="order-details-list-item-qty-details">
                  <span className="order-details-list-item-qty-details-label font-primary">
                    Qty -{' '}
                  </span>
                  <span className="font-primary-semibold">
                    {item?.quantity}
                  </span>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div className="order-details-list-item-qty-root">
            {showOrderEdit ? (
              <div className="order-details-list-item-qty-selector-container">
                {item?.isDeleting ? (
                  <TailSpin
                    width={15}
                    height={15}
                    wrapperClass="order-details-list-item-qty-selector-delete"
                  />
                ) : (
                  <button
                    type="button"
                    aria-label="Delete item"
                    onClick={(e) =>
                      handleDelete(e, {
                        ...item,
                        poFileId:
                          orderDetails?.poFileIds?.[0] ??
                          orderDetails?.poFileId,
                      })
                    }
                    className="order-details-list-item-qty-selector-delete"
                    style={{
                      border: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <img src={SVGS.DeleteRed} alt="" />
                  </button>
                )}
                {isNumber(item.productVariantId) && (
                  <div
                    className={`order-details-list-item-qty-selector font-primary-semibold ${
                      shouldDisableQtyControl
                        ? 'order-details-list-item-qty-selector-disabled'
                        : ''
                    }`}
                  >
                    <img
                      src={SVGS.MinuxIconBlue}
                      alt="Minus"
                      onClick={() => {
                        if (shouldDisableQtyControl) return;
                        onDecrement(item, seller?.workspaceId, inviteId);
                      }}
                    />
                    {item?.isLoading ? (
                      <div className="order-details-list-item-qty-selector-loader">
                        <LoadingSpinner size={15} />
                      </div>
                    ) : (
                      <input
                        className="order-details-list-item-qty-selector-input font-primary-semibold"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*[.,]?[0-9]+"
                        disabled={shouldDisableQtyControl}
                        key={item?.productVariantId}
                        onKeyDown={(e) => {
                          e.key === 'Enter' &&
                            onChangeQuantity(
                              item,
                              Number(quantityMap[quantityKey] ?? 0),
                              seller?.workspaceId,
                              inviteId
                            );
                        }}
                        onBlur={() =>
                          onChangeQuantity(
                            item,
                            Number(quantityMap[quantityKey] ?? 0),
                            seller?.workspaceId,
                            inviteId
                          )
                        }
                        onChange={(e) => {
                          if (!isNaN(Number(e.target.value))) {
                            let initialQuantiyMap = quantityMap;
                            if (item?.poFileLineId) {
                              initialQuantiyMap[item?.poFileLineId] = Number(
                                e.target.value
                              );
                            } else {
                              //@ts-ignore
                              initialQuantiyMap[item?.orderLineId] = Number(
                                e.target.value
                              );
                            }
                            setQuantityMap({ ...initialQuantiyMap });
                          }
                        }}
                        value={quantityMap[quantityKey]}
                      />
                    )}
                    <img
                      src={SVGS.AddIconBlue}
                      alt="plus"
                      onClick={() => {
                        if (shouldDisableQtyControl) return;
                        onIncrement(item, seller?.workspaceId, inviteId);
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <></>
            )}
            <span className="order-details-list-item-qty-total font-primary">
              {!showOrderEdit ? (
                <span className="order-details-list-item-qty-total-label font-primary">
                  {`Total Amount: `}
                </span>
              ) : (
                <></>
              )}
              {showPrices && (
                <span className="order-details-list-item-info-price-container-single-item font-primary">
                  {getFormattedAmount(item?.discountedTotal)}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  });
};
